import React from 'react';
import { Button } from 'equasion-lib';
import { FiLayers, FiPlus, FiMinusCircle, FiAlertCircle, FiX } from 'react-icons/fi';
import './ShippingBundlePanel.scss';

// Warehouse is on react-icons 4.x; RiArchiveStackLine only shipped in 5.x. Same Remix path.
const RiArchiveStackLine = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M4 5H20V3H4V5ZM20 9H4V7H20V9ZM3 11H10V13H14V11H21V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V11ZM16 13V15H8V13H5V19H19V13H16Z" />
  </svg>
);

/**
 * The bundled-shipping affordance on the warehouse order-summary page, rendered as a CONSIGNMENT
 * MANIFEST: a tape-striped slab that lists the orders travelling together, in the monospaced
 * tabular style of a printed packing manifest. One component, three states, driven entirely by
 * the `shippingBundleContext` read:
 *
 *   IN BUNDLE  — this order is a member. Shows the manifest and a "remove this order" escape.
 *   JOIN       — the client already has an open bundle. Shows ITS manifest and an "add" action.
 *   SET ASIDE  — the client has other orders in the warehouse. Lists them and offers to hold this
 *                order back for them.
 *
 * Anything else renders nothing: bundling is self-gating, and an operator with nothing to bundle
 * should see the page exactly as they always have.
 *
 * Handheld-first: every tap target is ≥ var(--tap-min), every row is a single line, and the
 * ineligible reasons are the server's own operator-facing strings, unedited.
 */

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const STATUS_LABEL = {
  // Combined at the end of Pick or at Verify: in the bundle, not packed yet.
  picking: 'picking',
  shipping: 'ready',
  delayed_ship: 'backordered',
  delayed_ship_draft: 'B/O Queue',
};

// Picking rows already say "picking". Repeating this on every one of them adds nothing.
const NOT_YET_SHIPPING_REASON = 'Only orders at the shipping stage can be bundled.';

/** Ready (shipping) siblings first; everything else keeps the order it arrived in. */
const readyFirst = (orders: any[]) =>
  [...orders]
    .map((order) =>
      order.reason === NOT_YET_SHIPPING_REASON ? { ...order, reason: undefined } : order,
    )
    .sort((a, b) => Number(a.vendorStatus !== 'shipping') - Number(b.vendorStatus !== 'shipping'));

// `: any`, matching SalesOrderList's OrderDataRow — the warehouse app's tsconfig does not gate
// the build and its JSX typing rejects `key` on inferred/explicit prop types alike.
function ManifestRow({ order, isCurrent, index, onRemove, busy }: any) {
  const status = STATUS_LABEL[order.vendorStatus] || order.vendorStatus;
  return (
    <li
      className={[
        'manifest-row',
        isCurrent && 'is-current',
        !order.bundleable && !order.inBundle && 'is-ineligible'
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ animationDelay: `${60 + index * 40}ms` }}
    >
      <span className="so-number">{order.vendorSalesOrderNumber}</span>
      {isCurrent ? (
        <span className="row-tag">this order</span>
      ) : (
        <span className={`row-status status-${order.vendorStatus}`}>{status}</span>
      )}
      <span className="row-count">{plural(order.itemCount, 'item', 'items')}</span>
      {!!onRemove && (
        <button
          type="button"
          className="row-remove"
          disabled={busy}
          onClick={() => onRemove(order)}
          aria-label={`Remove SO #${order.vendorSalesOrderNumber} from the bundle`}
        >
          <FiX aria-hidden="true" />
        </button>
      )}
      {!order.bundleable && !order.inBundle && order.reason && (
        <span className="row-reason">
          <FiAlertCircle /> {order.reason}
        </span>
      )}
    </li>
  );
}

const ShippingBundlePanel = ({
  context,
  currentOrder,
  clientName,
  busy = false,
  onSetAside,
  onJoin,
  onRemove,
  // Remove ANOTHER member (e.g. one combined at Pick / Verify that will not be ready in time).
  onRemoveOther = undefined as undefined | ((order: any) => void)
}) => {
  if (!context) return null;
  const { eligibility, bundle, joinableBundle, otherOrdersInFulfillment = [] } = context;
  const currentOrderId = currentOrder?.id;
  const currentRow = {
    id: currentOrderId,
    vendorSalesOrderNumber: currentOrder?.vendorSalesOrderNumber,
    vendorStatus: currentOrder?.vendorStatus,
    itemCount: (currentOrder?.orderItems || []).reduce((a, { quantity }) => a + quantity, 0),
    bundleable: true,
    inBundle: true
  };

  // ── IN BUNDLE ────────────────────────────────────────────────────────────────────────────────
  if (bundle) {
    const members = bundle.orders || [];
    return (
      <section className="shipping-bundle-panel is-member" aria-label="Shipping bundle">
        <header className="panel-eyebrow">
          <RiArchiveStackLine aria-hidden="true" />
          <span className="eyebrow-label">Shipping bundle</span>
          <span className="eyebrow-id">{bundle.bundleNumber}</span>
        </header>
        <ol className="manifest">
          {members.map((order, i) => (
            <ManifestRow
              key={order.id}
              order={order}
              isCurrent={order.id === currentOrderId}
              index={i}
              busy={busy}
              onRemove={
                onRemoveOther && order.id !== currentOrderId ? onRemoveOther : undefined
              }
            />
          ))}
        </ol>
        <footer className="panel-foot">
          <p className="foot-summary">
            <strong>{plural(members.length, 'order', 'orders')}</strong> ship as one parcel —
            one label, one tracking number. Each order keeps its own documents.
          </p>
          <Button
            type="link-subtle"
            size="compact"
            iconBefore={<FiMinusCircle />}
            disabled={busy}
            onClick={onRemove}
          >
            Remove this order from the bundle
          </Button>
        </footer>
      </section>
    );
  }

  // ── JOIN AN OPEN BUNDLE ──────────────────────────────────────────────────────────────────────
  if (joinableBundle && eligibility?.bundleable) {
    const members = joinableBundle.orders || [];
    return (
      <section className="shipping-bundle-panel is-offer" aria-label="Open shipping bundle">
        <header className="panel-eyebrow">
          <FiLayers aria-hidden="true" />
          <span className="eyebrow-label">Open bundle for {clientName}</span>
          <span className="eyebrow-id">{joinableBundle.bundleNumber}</span>
        </header>
        <ol className="manifest">
          {members.map((order, i) => (
            <ManifestRow key={order.id} order={order} isCurrent={false} index={i} />
          ))}
          <ManifestRow order={currentRow} isCurrent index={members.length} />
        </ol>
        <footer className="panel-foot">
          <p className="foot-summary">
            Add this order and ship all{' '}
            <strong>{plural(members.length + 1, 'order', 'orders')}</strong> together under one
            label.
          </p>
          <Button
            type="primary"
            fullWidth
            iconBefore={<FiPlus />}
            disabled={busy}
            onClick={() => onJoin(joinableBundle.id)}
          >
            Add to Bundle {joinableBundle.bundleNumber}
          </Button>
        </footer>
      </section>
    );
  }

  // ── SET ASIDE ────────────────────────────────────────────────────────────────────────────────
  if (otherOrdersInFulfillment.length > 0) {
    const bundleableSiblings = otherOrdersInFulfillment.filter((o) => o.bundleable);
    const canSetAside = eligibility?.bundleable && bundleableSiblings.length > 0;
    return (
      <section className="shipping-bundle-panel is-offer" aria-label="Bundled shipping">
        <header className="panel-eyebrow">
          <span className="eyebrow-label">Same client, more orders</span>
          <span className="eyebrow-id">
            {plural(otherOrdersInFulfillment.length, 'other', 'others')}
          </span>
        </header>
        <p className="panel-lead">
          <strong>{clientName}</strong> has{' '}
          {plural(otherOrdersInFulfillment.length, 'other order', 'other orders')} in the
          warehouse right now.
        </p>
        <ol className="manifest">
          {readyFirst(otherOrdersInFulfillment).map((order, i) => (
            <ManifestRow key={order.id} order={order} isCurrent={false} index={i} />
          ))}
        </ol>
        <footer className="panel-foot">
          {canSetAside ? (
            <>
              <p className="foot-summary">
                Hold this order back and ship it with them as one parcel when they reach
                shipping.
              </p>
              <Button
                type="primary"
                fullWidth
                iconBefore={<RiArchiveStackLine />}
                disabled={busy}
                onClick={onSetAside}
              >
                Set Aside for Bundled Shipping
              </Button>
            </>
          ) : (
            <p className="foot-summary is-muted">
              <FiAlertCircle />{' '}
              {eligibility?.bundleable
                ? 'None of these orders can share a shipment with this one.'
                : eligibility?.reason}
            </p>
          )}
        </footer>
      </section>
    );
  }

  return null;
};

export default ShippingBundlePanel;
